<template>
  <div class="invitation-response">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <div class="card mt-5">
            <div class="card-header">
              <h3 class="text-center">{{ $t("invitation.title") }}</h3>
            </div>
            <div class="card-body">
              <!-- Loading state -->
              <div v-if="isLoading" class="text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t("common.loading") }}</span>
                </div>
                <p class="mt-2">{{ $t("invitation.checking") }}</p>
              </div>

              <!-- Main content when not loading -->
              <template v-else>
                <!-- Show accept/reject buttons only if not expired and not responded -->
                <div v-if="!hasResponded && !isExpired" class="text-center mb-4">
                  <h4 class="mb-3">{{ $t("invitation.welcome") }}</h4>
                  <p class="mb-3">{{ $t("invitation.description") }}</p>
                  <p class="text-muted mb-4">{{ $t("invitation.accept_info") }}</p>
                  <div class="d-flex justify-content-center gap-3">
                    <button
                      v-if="!showForm"
                      class="btn btn-success"
                      @click="showAcceptForm"
                    >
                      <i class="bi bi-check-circle"></i> {{ $t("invitation.accept") }}
                    </button>
                    <button v-if="!showForm" class="btn btn-danger" @click="handleReject">
                      <i class="bi bi-x-circle"></i> {{ $t("invitation.reject") }}
                    </button>
                  </div>
                </div>

                <form v-if="showForm" @submit.prevent="handleSubmit" class="mt-4">
                  <h4 class="mb-3">Complete Your Registration</h4>
                  <p class="mb-3">
                    Please provide the following details to complete your registration.
                  </p>
                  <p class="text-muted mb-4">
                    Your information will be used to create your account and set up your
                    profile.
                  </p>

                  <div class="mb-3">
                    <label for="name" class="form-label">Full Name</label>
                    <input
                      type="text"
                      class="form-control"
                      id="name"
                      v-model="formData.full_name"
                      required
                    />
                  </div>
                  <div class="mb-3">
                    <label for="email" class="form-label">Email</label>
                    <input
                      type="email"
                      class="form-control"
                      id="email"
                      v-model="formData.email"
                      required
                    />
                  </div>
                  <div class="d-grid">
                    <button type="submit" class="btn btn-primary">
                      Complete Registration
                    </button>
                  </div>
                </form>

                <div v-if="hasResponded" class="alert" :class="responseClass">
                  <h4 class="alert-heading mb-2">
                    <i
                      :class="
                        responseClass === 'alert-success'
                          ? 'bi bi-check-circle'
                          : responseClass === 'alert-warning'
                          ? 'bi bi-exclamation-triangle'
                          : 'bi bi-info-circle'
                      "
                    ></i>
                    {{
                      responseClass === "alert-success"
                        ? $t("invitation.success")
                        : responseClass === "alert-warning"
                        ? $t("invitation.expired")
                        : $t("invitation.information")
                    }}
                  </h4>
                  <p class="mb-0">{{ responseMessage }}</p>
                  <hr v-if="responseClass === 'alert-warning'" />
                  <p v-if="responseClass === 'alert-warning'" class="mb-0">
                    <i class="bi bi-envelope"></i> {{ $t("invitation.contact_support") }}
                    <a href="mailto:support@example.com">support@example.com</a>
                    {{ $t("invitation.for_assistance") }}
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import { setStorage } from "@/Util/auth";

export default {
  name: "InvitationResponse",
  data() {
    return {
      showForm: false,
      hasResponded: false,
      responseMessage: "",
      responseClass: "",
      invitationToken: "",
      isLoading: true,
      isExpired: false,
      formData: {
        full_name: "",
        email: "",
        invitation_status: "",
        code: "",
      },
    };
  },
  async created() {
    // Get token from URL query parameter
    this.invitationToken = this.$route.query.token;
    this.formData.code = this.invitationToken;

    if (!this.invitationToken) {
      this.responseMessage = "Invalid invitation link. No token provided.";
      this.responseClass = "alert-danger";
      this.hasResponded = true;
      this.isLoading = false;
      return;
    }

    try {
      // Check invitation status
      const response = await request({
        method: "get",
        url: `is-expired/${this.invitationToken}`,
      });
      console.log(response);
      this.isExpired = response.data.is_expired;
      const invitationStatus = response.data.invitation_status;
      console.log(this.isExpired);
      if (this.isExpired) {
        this.responseMessage =
          "This invitation link has expired. Please contact the administrator for a new invitation.";
        this.responseClass = "alert-warning";
        this.hasResponded = true;
      } else if (invitationStatus === "accepted") {
        this.responseMessage =
          "Your invitation has already been accepted. You can now log in to access your account. If you need any assistance, please contact our support team.";
        this.responseClass = "alert-success";
        this.hasResponded = true;
      }
    } catch (error) {
      this.responseMessage = "Error checking invitation status. Please try again later.";
      this.responseClass = "alert-danger";
      this.hasResponded = true;
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    showAcceptForm() {
      this.showForm = true;
      this.formData.invitation_status = "accepted";
    },
    async handleReject() {
      try {
        await this.submitResponse({
          invitation_status: "rejected",
          code: this.invitationToken,
        });
      } catch (error) {
        console.error("Error rejecting invitation:", error);
      }
    },
    async handleSubmit() {
      try {
        await this.submitResponse(this.formData);
      } catch (error) {
        console.error("Error submitting response:", error);
      }
    },
    async submitResponse(data) {
      try {
        const response = await request({
          method: "post",
          url: `accept-reject-invitation`,
          data: data,
        });

        if (data.invitation_status === "accepted" && response.token) {
          // Store token and user data
          setStorage("auth", JSON.stringify(response.data));
          this.$store.dispatch("user",response.data);
          this.responseMessage =
            "Thank you for accepting the invitation! You will be redirected to your dashboard.";
          this.responseClass = "alert-success";
          // Refresh the page to update the layout
          window.location.href = "/profile";
        } else {
          this.responseMessage = "You have rejected the invitation.";
          this.responseClass = "alert-info";
        }

        this.hasResponded = true;
        this.showForm = false;
      } catch (error) {
        this.responseMessage = "An error occurred. Please try again.";
        this.responseClass = "alert-danger";
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.invitation-response {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem 0;
}

.card {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.btn {
  padding: 0.5rem 2rem;
}

.gap-3 {
  gap: 1rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
